/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import "./style.css";
import "leaflet/dist/leaflet.css";

import lod_ from "lodash";
import { v4 as uuidv4 } from "uuid";

import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Step,
	StepLabel,
	Stepper
} from "@mui/material";
import MDButton from "components/Basics/MDButton";
import { t } from "i18next";
import { useEffect, useRef, useState } from "react";

import ShopInterfaceCompetitor from "./steps/Competitor";
import ShopInterfaceShops from "./steps/Shops";
import ShopInterfaceFinish from "./steps/Finish";

import FormAction from "redux-react/actions/formAction";
import GoogleActions from "redux-react/actions/googleActions";
import { useDispatch } from "react-redux";

/**
 * Custom shop interface form
 * @param {boolean} open Whether the interface is open
 * @param {Function} onClose Function to close the interface
 * @param {Function} refreshChart Function to refresh the chart after changes
 * @param {object} resource Resource information
 * @param {object} values Values containing action and payload
 * @returns {JSX.Element|null} The ShopInterface component or null if action is invalid
 */
const ShopInterface = ({ open, onClose, refreshChart = () => {}, resource, values }) => {
	const { action, payload } = values || {};

	const INTERFACE = {
		insert: (
			<ShopInterfaceInsert
				open={open}
				onClose={onClose}
				resource={resource}
				payload={payload}
				refreshChart={refreshChart}
			/>
		),
		update: (
			<ShopInterfaceInsert
				open={open}
				onClose={onClose}
				resource={resource}
				payload={payload}
				refreshChart={refreshChart}
			/>
		)
	};

	return INTERFACE[action] || null;
};

/**
 * Shop interface form for inserting or updating a shop
 * @param {boolean} open Whether the interface is open
 * @param {Function} onClose Function to close the interface
 * @param {Function} refreshChart Function to refresh the chart after changes
 * @param {object} resource Resource information
 * @param {object} payload Payload containing edit flag and _id
 * @returns {JSX.Element} The ShopInterfaceInsert component
 */
const ShopInterfaceInsert = ({ open, onClose, refreshChart, resource, payload }) => {
	const { edit = false, _id = null } = payload || {};

	const dispatch = useDispatch();
	const topContainer = useRef(null);

	const [loading, setLoading] = useState(true);

	const steps = [
		t("FORMS.INTERFACE.SHOPS.competitor"),
		t("FORMS.INTERFACE.SHOPS.shops"),
		t("FORMS.INTERFACE.SHOPS.finish")
	];
	const [activeStep, setActiveStep] = useState(0);

	const isLastStep = activeStep === steps.length - 1;

	const initialFormState = {
		// Form fields
		active: true,
		brand: "",
		name: "",
		competitors: [],
		address: "",
		googleID: "",
		coordinates: { lat: null, long: null },
		url: "",
		//
		competitorsGoogleID: [],
		// State
		places: [],
		nearbyShops: []
	};

	const [form, setForm] = useState(initialFormState);

	const [isStepValid, setIsStepValid] = useState(false);

	/**
	 * Handle form field changes
	 * @param {string} field The field name to update
	 * @param {any} value The new value for the field
	 */
	const handleChange = (field, value) => {
		setForm(prevForm => {
			lod_.set(prevForm, field, value);
			return { ...prevForm };
		});
	};

	/**
	 * Set the validity of the current step
	 * @param {boolean} valid Whether the step is valid
	 */
	const setStepValid = (valid = true) => {
		setIsStepValid(valid);
	};

	/**
	 * Go to next step
	 */
	const handleNext = () => {
		setActiveStep(activeStep + 1);
		setStepValid(false);
		topContainer?.current?.scrollIntoView();
	};
	/**
	 * Go to previous step
	 */
	const handleBack = () => {
		setActiveStep(activeStep - 1);
		setStepValid(false);
		topContainer?.current?.scrollIntoView();
	};

	/**
	 * Get the content component for the current step
	 * @param {number} step The current step index
	 * @returns {JSX.Element} The content component for the step
	 */
	function getStepContent(step) {
		switch (step) {
			case 0:
				return (
					<ShopInterfaceCompetitor
						form={form}
						handleChange={handleChange}
						setStepValid={setStepValid}
					/>
				);
			case 1:
				return (
					<ShopInterfaceShops
						form={form}
						handleChange={handleChange}
						setStepValid={setStepValid}
						resource={resource}
					/>
				);
			case 2:
				return (
					<ShopInterfaceFinish
						form={form}
						handleChange={handleChange}
						setStepValid={setStepValid}
					/>
				);
			default:
				return "Unknown step";
		}
	}

	/**
	 * Determine the label of the action button
	 * @returns {string} The label of the action button
	 */
	function getActionButtonLabel() {
		if (isLastStep) {
			if (edit === true) {
				return t("SETTINGS.edit");
			}
			return t("SETTINGS.add");
		} else {
			return t("SETTINGS.CHARTS.NEW.STEPS.next");
		}
	}

	/**
	 * Save the form data
	 */
	const onSave = () => {
		const competitors = form.nearbyShops
			.filter(shop => form.competitorsGoogleID.includes(shop.id))
			.map(shop => shop.shopCode);

		const payload = {
			active: form.active,
			brand: form.brand,
			code: form.googleID,
			name: form.name,
			address: form.address,
			googleID: form.googleID,
			coordinates: form.coordinates,
			url: null,
			competitors,
			configured: true,
			extra: {}
		};

		const onSuccess = () => {
			onClose();
			refreshChart();
		};

		let data = { values: payload, target: resource.storage.collection, unique: [] };

		if (edit === true) {
			dispatch(FormAction.updateItem(_id, data, onSuccess));
		} else {
			dispatch(FormAction.addItemEmpty(data, onSuccess));
		}
	};

	/**
	 * Initialize the interface, fetching data if editing an existing shop
	 */
	const initInterface = async () => {
		if (edit && !lod_.isNil(_id)) {
			// Fetch competitor item
			const item = await new Promise(resolve => {
				dispatch(
					FormAction.getItemsFromCollection(
						resource.storage.collection,
						{
							query: { _id }
						},
						res => {
							resolve(res?.items?.[0] || null);
						}
					)
				);
			});

			if (!lod_.isNil(item)) {
				// Fetch competitor item
				const competitors = [];

				const competitorsFromDatabase = await new Promise(resolve => {
					dispatch(
						FormAction.getItemsFromCollection(
							"shop",
							{
								query: { code: { $in: item.competitors || [] } },
								options: { limit: 100 }
							},
							res => {
								resolve(res?.items || []);
							}
						)
					);
				});

				for (let competitorFromDatabase of competitorsFromDatabase) {
					const googleID = competitorFromDatabase.googlePlaceID;

					const competitor = await new Promise(resolve => {
						dispatch(
							GoogleActions.getGooglePlaceByID(
								googleID,
								res => {
									resolve(res || null);
								},
								() => resolve(null)
							)
						);
					});

					if (!lod_.isNil(competitor)) {
						competitors.push(competitor);
					}
				}

				setForm(prev => ({
					...prev,
					active: item.active,
					brand: item.brand,
					name: item.name,
					address: item.address,
					googleID: item.googleID,
					coordinates: item.coordinates || { lat: null, long: null },
					competitorsGoogleID: competitors.map(c => c.id),
					url: item.url,
					competitors: item.competitors || [],
					nearbyShops: competitors
				}));
			}

			setActiveStep(2);
			setStepValid(true);
		} else {
			setForm(prev => initialFormState);
			setActiveStep(0);
			setStepValid(false);
		}

		setLoading(false);
	};

	useEffect(() => {
		initInterface();
	}, [open]);

	if (loading) {
		return null;
	}

	return (
		<Dialog
			open={open}
			fullWidth
			maxWidth="xl"
			style={{ height: "100%" }}
			PaperProps={{
				style: {
					height: "100%",
					width: "95%",
					maxWidth: "95%"
				}
			}}
		>
			<DialogTitle>
				<Stepper activeStep={activeStep} alternativeLabel>
					{steps.map(label => (
						<Step key={label}>
							<StepLabel>{label}</StepLabel>
						</Step>
					))}
				</Stepper>
			</DialogTitle>
			<DialogContent sx={{ display: "flex" }}>
				<div data-id="top-container" ref={topContainer}></div>
				{getStepContent(activeStep)}
			</DialogContent>
			<DialogActions>
				<MDButton variant="outlined" color="info" onClick={onClose}>
					{t("SETTINGS.cancel")}
				</MDButton>
				<MDButton
					disabled={activeStep === 0 || (edit === true && activeStep < 2)}
					variant="contained"
					color="light"
					onClick={handleBack}
				>
					{t("SETTINGS.CHARTS.NEW.STEPS.back")}
				</MDButton>
				<MDButton
					disabled={!isStepValid}
					variant="contained"
					color="info"
					onClick={!isLastStep ? handleNext : onSave}
				>
					{getActionButtonLabel()}
				</MDButton>
			</DialogActions>
		</Dialog>
	);
};

export default ShopInterface;
